// Settings.jsx — personal user preferences (not a CRUD module)
"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useAlert } from "context/AlertContext";
import { useTheme } from "../../../context/ThemeContext";
import { useSettings } from "../../../context/SettingsContext";
import Skeleton from "components/ui/Skeleton";
import { FaSave, FaUndo, FaSlidersH } from "react-icons/fa";

import apiSettings from "./api";
import apiUserSettings from "api/global-api/api-settings.js";
import { buildSettingsFormState, SETTINGS_FIELDS } from "./settings-shared";
import SettingsNav, { SETTINGS_SECTIONS } from "./settings-nav";
import AppearanceSection from "./appearance-section";
import RegionalSection from "./regional-section";
import WorkPreferencesSection from "./work-preferences-section";
import NotificationsSection from "./notifications-section";
import PrivacySection from "./privacy-section";

export default function Settings() {
  const { showAlert } = useAlert();
  const { setTheme } = useTheme();
  const { updateSettingsLocal } = useSettings();

  const [settingsId, setSettingsId] = useState(null);
  const [formData, setFormData] = useState(null);
  const [savedData, setSavedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timezones, setTimezones] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [activeSection, setActiveSection] = useState(SETTINGS_SECTIONS[0].id);

  const containerRef = useRef(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiUserSettings.getMySettings();
      const data = res?.data?.data;
      setSettingsId(data?.id ?? null);
      const state = buildSettingsFormState(data);
      setFormData(state);
      setSavedData(state);
    } catch (err) {
      showAlert({ type: "error", message: err?.response?.data?.message || "Could not load your settings." });
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const fetchOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [tzRes, curRes] = await Promise.allSettled([apiSettings.getTimezones(), apiSettings.getCurrencies()]);
      const tz = tzRes.status === "fulfilled" ? tzRes.value?.data?.data || [] : [];
      const cur = curRes.status === "fulfilled" ? curRes.value?.data?.data || [] : [];
      setTimezones(tz.filter((t) => !t.is_deleted && t.is_activated));
      setCurrencies(cur.filter((c) => !c.is_deleted && c.is_activated));
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchOptions();
  }, [fetchSettings, fetchOptions]);

  // Scroll-spy: a section is "active" once its heading has scrolled past the
  // top threshold — i.e. the last section header the user has scrolled by.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const THRESHOLD = 24;
    const onScroll = () => {
      const containerTop = container.getBoundingClientRect().top;
      let current = SETTINGS_SECTIONS[0].id;
      for (const { id } of SETTINGS_SECTIONS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const relativeTop = el.getBoundingClientRect().top - containerTop;
        if (relativeTop <= THRESHOLD) current = id;
      }
      setActiveSection(current);
    };

    onScroll();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => container.removeEventListener("scroll", onScroll);
  }, [loading]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Live preview: reflect the choice immediately instead of waiting for Save.
    if (name === "theme") {
      if (value === "SYSTEM") {
        const resolved = window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        setTheme(resolved);
      } else {
        setTheme(value === "DARK" ? "dark" : "light");
      }
    }
  };

  const isDirty = formData && savedData && JSON.stringify(formData) !== JSON.stringify(savedData);

  const handleDiscard = () => {
    setFormData(savedData);
    if (savedData?.theme && savedData.theme !== "SYSTEM") {
      setTheme(savedData.theme === "DARK" ? "dark" : "light");
    }
  };

  const handleSave = async () => {
    if (!settingsId) return;

    const payload = SETTINGS_FIELDS.reduce((acc, key) => {
      let val = formData[key];
      if (key === "timezone_fid" || key === "currency_fid") val = val === "" ? null : parseInt(val);
      if (key === "projects_per_page") val = parseInt(val) || 20;
      acc[key] = val;
      return acc;
    }, {});

    try {
      setSaving(true);
      const res = await apiUserSettings.updateSettings(settingsId, payload);
      if (res?.data?.status) {
        setSavedData(formData);
        // Sync the shared context locally with what we just saved — no
        // refetch, so nothing else in the app re-renders/reloads from this.
        // The update response has no `timezone` relation, so attach the picked
        // record from the options list (the header clock reads it). If the
        // list didn't load, keep the current record rather than dropping it.
        const pickedTz = timezones.find((t) => t.id === payload.timezone_fid);
        updateSettingsLocal({
          ...payload,
          ...(payload.timezone_fid == null ? { timezone: null } : pickedTz ? { timezone: pickedTz } : {}),
        });
        showAlert({ type: "success", message: "Settings saved." });
      } else {
        showAlert({ type: "warning", message: res?.data?.message || "Could not save settings." });
      }
    } catch (err) {
      showAlert({ type: "error", message: err?.response?.data?.message || "An unexpected error occurred." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="shrink-0 pt-3 pb-4">
        <div className="max-w-[100rem] mx-auto w-full flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-primary flex items-center gap-2">
              <FaSlidersH size={16} /> Settings
            </h1>
            <p className="text-sm text-text_secondary mt-0.5">Personalize how SwiftBoard works for you</p>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 pb-3 overflow-y-auto lg:overflow-visible">
        <div className="max-w-[100rem] mx-auto w-full lg:h-full">
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
              <Skeleton className="h-56 w-full" />
              <div className="space-y-4">
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-56 w-full" />
                <Skeleton className="h-72 w-full" />
              </div>
            </div>
          ) : (
            <div className="lg:h-full grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
              <div className="lg:h-fit lg:sticky lg:top-0">
                <SettingsNav activeId={activeSection} />
              </div>

              <div ref={containerRef} className="lg:h-full lg:overflow-y-auto space-y-4 pb-24 lg:pr-1">
                <AppearanceSection formData={formData} onChange={handleChange} />
                <RegionalSection
                  formData={formData}
                  onChange={handleChange}
                  timezones={timezones}
                  currencies={currencies}
                  loadingOptions={loadingOptions}
                />
                <WorkPreferencesSection formData={formData} onChange={handleChange} />
                <NotificationsSection formData={formData} onChange={handleChange} />
                <PrivacySection formData={formData} onChange={handleChange} />
              </div>
            </div>
          )}
        </div>
      </div>

      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 z-[90] border-t border-border bg-surface shadow-[0_-6px_20px_rgba(0,0,0,0.08)]">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-3">
            <p className="text-xs text-text_secondary">You have unsaved changes</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDiscard}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-text_secondary bg-surface border border-border hover:bg-surface_alt transition-colors disabled:opacity-50"
              >
                <FaUndo size={12} /> Discard
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-secondary hover:bg-secondary_hover transition-colors shadow-sm disabled:opacity-60"
              >
                <FaSave size={13} /> {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
